/**
 * Device PIN + biometric unlock helpers (Capacitor Android / iOS).
 * Never stores the master password.
 * PIN protects the user key; refresh token lives in app Preferences (updated on refresh).
 * Biometric stores a full snapshot in Android Keystore / iOS Keychain.
 */
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { NativeBiometric } from "@capgo/capacitor-native-biometric";
import {
  encryptAes256Hmac,
  decryptEncString,
  pbkdf2,
  stretchKey,
  type SymmetricKey,
} from "@ciphernest/crypto";
import type { UnlockSnapshot } from "@ciphernest/vault-core";

const BIO_SERVER = "ciphernest.vault";
const PIN_KEY_CIPHER = "ciphernest.pin.userKey";
const PIN_SALT_KEY = "ciphernest.pin.salt";
const PIN_ENABLED_KEY = "ciphernest.pin.enabled";
const BIO_ENABLED_KEY = "ciphernest.bio.enabled";
const META_KEY = "ciphernest.device.meta";
const PIN_ITERATIONS = 120_000;

type DeviceMeta = {
  email: string;
  refreshToken: string;
  baseUrl: string;
  deviceIdentifier: string;
};

export function isNativeApp() {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

async function prefGet(key: string) {
  const { value } = await Preferences.get({ key });
  return value;
}

async function prefSet(key: string, value: string) {
  await Preferences.set({ key, value });
}

async function prefDel(key: string) {
  await Preferences.remove({ key });
}

function b64FromBuf(buf: ArrayBuffer) {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]!);
  return btoa(s);
}

function bufFromB64(s: string) {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}

async function derivePinKey(pin: string, saltB64: string): Promise<SymmetricKey> {
  const bits = await pbkdf2(pin, bufFromB64(saltB64), PIN_ITERATIONS, 32);
  return stretchKey(bits);
}

async function saveMeta(snap: UnlockSnapshot) {
  const meta: DeviceMeta = {
    email: snap.email,
    refreshToken: snap.refreshToken,
    baseUrl: snap.baseUrl,
    deviceIdentifier: snap.deviceIdentifier,
  };
  await prefSet(META_KEY, JSON.stringify(meta));
}

async function loadMeta(): Promise<DeviceMeta | null> {
  const raw = await prefGet(META_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as DeviceMeta;
  } catch {
    return null;
  }
}

export async function isPinEnabled() {
  return (await prefGet(PIN_ENABLED_KEY)) === "1";
}

export async function isBioEnabled() {
  return (await prefGet(BIO_ENABLED_KEY)) === "1";
}

export async function clearDeviceUnlock() {
  await prefDel(PIN_KEY_CIPHER);
  await prefDel(PIN_SALT_KEY);
  await prefDel(PIN_ENABLED_KEY);
  await prefDel(BIO_ENABLED_KEY);
  await prefDel(META_KEY);
  if (isNativeApp()) {
    try {
      await NativeBiometric.deleteCredentials({ server: BIO_SERVER });
    } catch {
      /* none */
    }
  }
}

/** Keep refresh token current after vault refresh / unlock (PIN + bio). */
export async function persistUnlockMeta(snap: UnlockSnapshot) {
  if ((await isPinEnabled()) || (await isBioEnabled())) {
    await saveMeta(snap);
  }
  if (await isBioEnabled()) {
    await refreshBioBlobIfEnabled(snap);
  }
}

export async function enablePinUnlock(pin: string, snap: UnlockSnapshot) {
  if (pin.length < 4 || pin.length > 8 || !/^\d+$/.test(pin)) {
    throw new Error("PIN must be 4–8 digits");
  }
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const saltB64 = b64FromBuf(salt.buffer);
  const key = await derivePinKey(pin, saltB64);
  const cipher = await encryptAes256Hmac(snap.userKeyB64, key);
  await prefSet(PIN_SALT_KEY, saltB64);
  await prefSet(PIN_KEY_CIPHER, cipher);
  await prefSet(PIN_ENABLED_KEY, "1");
  await saveMeta(snap);
}

export async function unlockWithPin(pin: string): Promise<UnlockSnapshot> {
  const salt = await prefGet(PIN_SALT_KEY);
  const cipher = await prefGet(PIN_KEY_CIPHER);
  const meta = await loadMeta();
  if (!salt || !cipher || !meta) throw new Error("PIN unlock is not set up");
  try {
    const key = await derivePinKey(pin, salt);
    const userKeyB64 = await decryptEncString(cipher, key);
    if (!userKeyB64) throw new Error("Wrong PIN");
    return {
      email: meta.email,
      refreshToken: meta.refreshToken,
      userKeyB64,
      baseUrl: meta.baseUrl,
      deviceIdentifier: meta.deviceIdentifier,
    };
  } catch (err) {
    if (err instanceof Error && err.message === "PIN unlock is not set up") throw err;
    throw new Error("Wrong PIN");
  }
}

export async function disablePinUnlock() {
  await prefDel(PIN_KEY_CIPHER);
  await prefDel(PIN_SALT_KEY);
  await prefDel(PIN_ENABLED_KEY);
  if (!(await isBioEnabled())) await prefDel(META_KEY);
}

export async function biometricAvailable(): Promise<boolean> {
  if (!isNativeApp()) return false;
  try {
    const r = await NativeBiometric.isAvailable();
    return Boolean(r.isAvailable);
  } catch {
    return false;
  }
}

export async function enableBiometricUnlock(snap: UnlockSnapshot) {
  if (!isNativeApp()) throw new Error("Biometrics require the Android app");
  const avail = await NativeBiometric.isAvailable();
  if (!avail.isAvailable) {
    throw new Error("Fingerprint / biometrics not available on this device");
  }
  await NativeBiometric.verifyIdentity({
    reason: "Enable fingerprint unlock for CipherNest",
    title: "CipherNest",
    subtitle: "Confirm it's you",
    negativeButtonText: "Cancel",
  });
  await NativeBiometric.setCredentials({
    username: snap.email,
    password: JSON.stringify(snap),
    server: BIO_SERVER,
  });
  await prefSet(BIO_ENABLED_KEY, "1");
  await saveMeta(snap);
}

export async function unlockWithBiometric(): Promise<UnlockSnapshot> {
  if (!isNativeApp()) throw new Error("Biometrics require the Android app");
  await NativeBiometric.verifyIdentity({
    reason: "Unlock your CipherNest vault",
    title: "CipherNest",
    subtitle: "Use fingerprint",
    negativeButtonText: "Cancel",
  });
  const creds = await NativeBiometric.getCredentials({ server: BIO_SERVER });
  if (!creds.password) throw new Error("No biometric unlock saved");
  const fromBio = JSON.parse(creds.password) as UnlockSnapshot;
  const meta = await loadMeta();
  // Prefer latest refresh token from meta if bio blob is stale
  if (meta?.refreshToken) {
    return { ...fromBio, refreshToken: meta.refreshToken, email: meta.email };
  }
  return fromBio;
}

export async function disableBiometricUnlock() {
  await prefDel(BIO_ENABLED_KEY);
  if (isNativeApp()) {
    try {
      await NativeBiometric.deleteCredentials({ server: BIO_SERVER });
    } catch {
      /* ignore */
    }
  }
  if (!(await isPinEnabled())) await prefDel(META_KEY);
}

export async function refreshBioBlobIfEnabled(snap: UnlockSnapshot) {
  if (!(await isBioEnabled()) || !isNativeApp()) return;
  try {
    await NativeBiometric.setCredentials({
      username: snap.email,
      password: JSON.stringify(snap),
      server: BIO_SERVER,
    });
    await saveMeta(snap);
  } catch {
    /* may need re-enable */
  }
}
